import AutorenewIcon from '@mui/icons-material/Autorenew'
import clsx from 'clsx'
import { where } from 'firebase/firestore'
import { sumBy } from 'lodash'
import { FC, useEffect, useState } from 'react'
import Select, { components } from 'react-select'
import * as XLSX from 'xlsx'

import InfiniteScrollDataTable from '../../../../../../../components/common/tables/InfiniteScrollDataTable'
import { ReportsTabWrapper } from '../ReportsTabWrapper/ReportsTabWrapper'

import useToasterHelper from '../../../../../../../helpers/ToasterHelper'
import { getUserFullName, toFixed } from '../../../../../../../helpers/helpers'

import { useAppSelector } from '../../../../../../../store/hooks'
import { selectUserReducer } from '../../../../../../../store/user/userSlice'

import TimeLib from '../../../../../../../lib/Time'

import { IUSEFESection, sectionCodesByDisciplines } from '../../../../data/reports/USEF'
import { IReports } from '../../ManageClinicReportsRoot'

import FirestoreService from '../../../../../../../services/firestoreService'

import { EventResultsReportsModel } from '../../../../../../../models/event-results-reports/event-results-reports.model'
import { IEventInterface } from '../../../../../../../models/events/event.interface'
import { IHorseData } from '../../../../../../../models/horse/horse.interface'
import { getConvertedData } from '../../../../../../../models/interface.helper'
import { IRegistrationByDayInterface } from '../../../../../../../models/registrations-by-day/registrationByDay.interface'

import { CONST } from '../../../../../../../const/const'
import {
  BREED_AFFILIATE_FIELDS,
  DISCIPLINE_AFFILIATE_FIELDS,
} from '../../../../../../../const/discipline-memberships-const'
import { MESSAGES_CONST } from '../../../../../../../const/messages-const'
import {
  customSelectStyles,
  MyOptionType,
} from '../../../../../../../customStyles/ReactSelectCustomStyle'
import { getAllRegistrationFees } from '../../../../../../../helpers/registrationFees'
import { formatOptionLabel } from '../../../../../../../helpers/select'
import { RegistrationFeesType } from '../../../../../../../models/event-fees/event-fees.interface'
import { IScoreCardInterface } from '../../../../../../../models/score-cards/score-cards.interface'
import { IRiderTeamMemberInterface } from '../../../../../../../models/rider-team-member/riderTeamMember.interface'

import { selectEventDetails } from '../../../../../../../store/events/eventsSlice'
import {
  calculateClassStatistics,
  convertFenceHeightToDisplay,
  createClassToScoreSheetMapping,
  createFenceTypeClassMapping,
  findFenceHeightByScoreCard,
  getHorseBreedAffiliateNumbers,
  getHorseDisciplineAffiliateNumbers,
  getUserAffiliateNumbers,
  parseAddress,
} from '../../eventResultsReportUtils'
import { USEF_REQUIRED_COLUMNS, USEFRecord } from './usef-report-columns'
import { MEMBER_ROLES } from '../../../../../../../const/ui/ui-register.const'

import { horseBreedList } from '../../../../../../../fakeData/horseBreedList'
import { CountryList } from '../../../../../../../fakeData/countryList'
import { amateurList } from '../../../../../../../fakeData/amateurList'
import UserService from '../../../../../../../services/firebase-service/users/userService'

const COLLECTIONS = CONST.DATA.FIRESTORE.V01.COLLECTIONS

interface IUSEFResultsReport {
  class: { uuid: string; name: string }
  discipline: string
  USEFSection: { code: string; name: string }
}

interface USEFResultsReportProps {
  event: IEventInterface | null
  registrationFees: any
  activeOption: IReports
  setActiveOption: (value: IReports) => void
  setActiveResult: (value: string) => void
  registeredUsersByDay: IRegistrationByDayInterface[] | null
  horses: IHorseData[] | null
  hunterJumperScoreCards: IScoreCardInterface[] | null
  riderTeamMembers: IRiderTeamMemberInterface[]
}

export const USEFResultsReport: FC<USEFResultsReportProps> = ({
  event,
  registrationFees,
  activeOption,
  setActiveOption,
  setActiveResult,
  registeredUsersByDay,
  horses,
  hunterJumperScoreCards,
  riderTeamMembers,
}) => {
  const toastFunctions = useToasterHelper()
  const eventDetails = useAppSelector(selectEventDetails)
  const user = useAppSelector(selectUserReducer)

  const [rows, setRows] = useState<IUSEFResultsReport[]>([])
  const [
    isReadyToExport,
    // setIsReadyToExport
  ] = useState(true)
  const [discipline, setDiscipline] = useState('')
  const [reportId, setReportId] = useState('')
  const [loading, setLoading] = useState(false)

  useEffect(() => {
    if (discipline) {
      const newRows = rows.map((row) => ({
        class: row.class,
        discipline: discipline,
        USEFSection: { code: '', name: '' },
      }))
      setRows(newRows)
      saveEventReport(newRows).then()
    }
  }, [discipline])

  useEffect(() => {
    getEventReport().then()
  }, [registrationFees])

  const saveEventReport = async (rows: IUSEFResultsReport[]) => {
    try {
      if (reportId) {
        await FirestoreService.updateItem(COLLECTIONS.EVENT_RESULTS_REPORTS.NAME, reportId, {
          rows,
          modified: TimeLib.utcTimestamp(),
        })
      } else {
        const ref = await FirestoreService.createItem(
          COLLECTIONS.EVENT_RESULTS_REPORTS.NAME,
          new EventResultsReportsModel({
            userId: user.userId,
            eventId: event?.id ?? '',
            eventName: event?.eventName ?? '',
            reportType: 'USEF Results',
            rows,
          }).toFirestore()
        )
        setReportId(ref.id)
      }
    } catch (error) {
      toastFunctions.error({
        message: MESSAGES_CONST.SOMETHING_WENT_WRONG,
      })
    }
  }

  const getEventReport = async () => {
    setLoading(true)

    try {
      if (!registrationFees?.registrationFees) {
        setLoading(false)
        return
      }

      // always generate the complete rows from registration fees
      // this ensures we capture both parent classes and any nested children classes,
      // even if the class structure has changed since the last save
      const flattenedFees = getAllRegistrationFees(registrationFees.registrationFees)

      const rows_: IUSEFResultsReport[] = flattenedFees.map((fee) => ({
        class: { uuid: fee.uuid, name: fee.name || '' },
        discipline: '',
        USEFSection: { code: '', name: '' },
      }))

      // check if there is a saved report to merge
      const eventResultsReportSnaps = await FirestoreService.filterItems(
        COLLECTIONS.EVENT_RESULTS_REPORTS.NAME,
        [
          where(COLLECTIONS.EVENT_RESULTS_REPORTS.FIELDS.EVENT_ID.KEY, '==', event?.id),
          where(COLLECTIONS.EVENT_RESULTS_REPORTS.FIELDS.USER_ID.KEY, '==', user.userId),
          where(COLLECTIONS.EVENT_RESULTS_REPORTS.FIELDS.REPORT_TYPE.KEY, '==', 'USEF Results'),
        ]
      )

      if (eventResultsReportSnaps.size > 0) {
        const savedReport = getConvertedData(
          EventResultsReportsModel.fromFirestoreDoc(eventResultsReportSnaps.docs[0]).toObject()
        )

        // merge saved configs with fresh class list to ensure no stale data
        const mergedRows = rows_.map((freshRow) => {
          const savedRow = savedReport.rows?.find((sr) => sr.class.uuid === freshRow.class.uuid)
          return savedRow
            ? {
                ...freshRow,
                discipline: savedRow.discipline,
                USEFSection: savedRow.USEFSection,
              }
            : freshRow
        })

        setRows(mergedRows)
        setReportId(savedReport?.id ?? '')
      } else {
        setRows(rows_)
      }
    } catch (error) {
      console.error('Error fetching event report:', error)
      toastFunctions.error({
        message: MESSAGES_CONST.SOMETHING_WENT_WRONG,
      })
      setRows([])
    } finally {
      setLoading(false)
    }
  }

  const onExport = async () => {
    if (isReadyToExport) {
      const requiredColumns = USEF_REQUIRED_COLUMNS
      // type for record based on the required columns
      const data: USEFRecord[] = []

      const registrationMap = new Map()

      registeredUsersByDay?.forEach((registration) => {
        const key = `${registration.riderId}-${registration.horseId}-${registration.uuid}`
        const existing = registrationMap.get(key)

        if (!existing) {
          registrationMap.set(key, [registration])
        } else if (existing[0].isScratched || registration.isScratched) {
          // id either is scratched - keep both - we show scratched in the report
          existing.push(registration)
        } else {
          // Both are active AND not scratched - keep the most recently created
          const existingDate = existing[0].created ? new Date(existing[0].created) : new Date(0)
          const currentDate = registration.created ? new Date(registration.created) : new Date(0)

          if (currentDate > existingDate) {
            registrationMap.set(key, [registration])
          }
          // if currentDate <= existingDate, keep the existing one (do nothing)
        }
      })

      const filteredRegistrations: IRegistrationByDayInterface[] = Array.from(
        registrationMap.values()
      ).flat()

      const classStatsMap = calculateClassStatistics(filteredRegistrations)

      const flattenedFees = getAllRegistrationFees(registrationFees.registrationFees || [])

      const fenceTypeClassMapping = createFenceTypeClassMapping(
        flattenedFees,
        hunterJumperScoreCards || []
      )

      const scoreSheetMapping = createClassToScoreSheetMapping(
        flattenedFees,
        registeredUsersByDay || []
      )

      let maxJudges = 0

      filteredRegistrations.forEach((registeredUserByDay) => {
        const length = registeredUserByDay?.score?.judges?.length ?? 0
        if (maxJudges < length) maxJudges = length
      })

      const ids: Set<string> = new Set()
      filteredRegistrations.forEach((item) => {
        if (item.riderId) ids.add(item.riderId)
      })
      riderTeamMembers.forEach((item) => {
        if (item.teamMemberId) ids.add(item.teamMemberId)
      })
      const usersInDb = await UserService.getByIds([...ids])

      // main loop
      filteredRegistrations.forEach((registeredUserByDay) => {
        const prizeMoneyTotal = flattenedFees.find(
          (registrationFee) => registrationFee.uuid === registeredUserByDay.uuid
        )?.prizeMoney

        const classData = flattenedFees.find(
          (fee: RegistrationFeesType) => fee.uuid === registeredUserByDay.uuid
        )

        const horse = horses?.find((horse) => horse.id === registeredUserByDay.horseId)
        const rider = usersInDb?.find((user) => user.id === registeredUserByDay.riderId)

        const teamMemberWithRider = riderTeamMembers.map((trainer) => ({
          trainer,
          rider:
            registeredUsersByDay?.find(
              (item) =>
                item.registrationDocId === trainer.registrationDocId &&
                item.riderId === trainer.riderId &&
                item.horsesSelected.some((horse) => horse.id === trainer.horseId)
            ) || null,
        }))

        const horseRiderOwnerId = teamMemberWithRider?.find(
          (member) =>
            member.trainer.teamMemberRole === MEMBER_ROLES.OWNER &&
            member.rider?.riderId === registeredUserByDay.riderId &&
            member.rider?.horsesSelected?.[0] &&
            member.rider?.horsesSelected?.find((horse) =>
              registeredUserByDay.horsesSelected?.find((item) => item.id === horse.id)
            )
        )?.trainer?.teamMemberId
        const horseRiderOwner = usersInDb?.find((user) => user.id === horseRiderOwnerId)

        const horseRiderTrainerId = teamMemberWithRider?.find(
          (member) =>
            member.trainer.teamMemberRole === MEMBER_ROLES.TRAINER &&
            member.rider?.riderId === registeredUserByDay.riderId &&
            member.rider?.horsesSelected?.[0] &&
            member.rider?.horsesSelected?.find((horse) =>
              registeredUserByDay.horsesSelected?.find((item) => item.id === horse.id)
            )
        )?.trainer?.teamMemberId

        const horseRiderTrainer = usersInDb?.find((user) => user.id === horseRiderTrainerId)

        // base record with empty values for all required columns
        const record = {} as USEFRecord

        // Initialize all fields with empty strings
        requiredColumns.forEach((column) => {
          record[column] = ''
        })

        // Calculate judge information
        type JudgeInfo = {
          [key: string]: string | number
        }

        const judgeInfo: JudgeInfo = {}

        registeredUserByDay.score?.judges?.forEach((judge, index) => {
          let totalPoints = 0

          judge.sections.forEach((section) =>
            section.rows.forEach((row) => {
              if (row.coefficient) {
                totalPoints += Number(row.coefficient) * Number(row.score)
              } else {
                totalPoints += Number(row.score)
              }
            })
          )

          const maxPoints = sumBy(judge.sections, (section) => section.max)

          const firstName = `Judge ${index + 1} First Name` as string
          const lastName = `Judge ${index + 1} Last Name` as string
          const percentage = `Judge ${index + 1} Percentage` as string
          const score = `Judge ${index + 1} Score (Optional except Dressage)` as string

          judgeInfo[firstName] = judge.name.split(' ')[0] || ''
          judgeInfo[lastName] = judge.name.split(' ')[1] || ''
          judgeInfo[percentage] = maxPoints ? toFixed((totalPoints / maxPoints) * 100, 3) : ''
          judgeInfo[score] = totalPoints || ''
        })

        // Calculate dressage percentage
        let dressagePercentageTotal = 0
        let max = 0
        if (registeredUserByDay.score?.judges) {
          max = sumBy(registeredUserByDay.score.judges[0]?.sections, (section) => section.max)
          if (max) {
            dressagePercentageTotal =
              (Number(registeredUserByDay.score?.totalPoints ?? 0) / max) * 100
          }
        }

        // Fill in the data we have - won't have all USEF col data yet
        // Event and Class details
        record['COMP YEAR'] = new Date(registeredUserByDay.created as Date).getFullYear() || ''
        record['USEF Comp ID number'] = eventDetails.bodyCompId || ''
        record['Class Number'] = classData?.order || ''
        record['Class Title'] = registeredUserByDay.registrationByDayName || ''

        const registrationClassName = registeredUserByDay.registrationByDayName || ''

        const scoreCardMappingForRegistrationClass =
          scoreSheetMapping[registrationClassName]?.scoreSheetName || ''

        const fenceHeightRaw = findFenceHeightByScoreCard(
          scoreCardMappingForRegistrationClass || '',
          hunterJumperScoreCards || []
        )
        const formattedFenceHeight = convertFenceHeightToDisplay(fenceHeightRaw)
        record['Fence Height'] = formattedFenceHeight
        record["Hunter Fence Height 3, 3'3, 3'6, 3'9"] = formattedFenceHeight

        let formattedFenceType = ''
        if (
          registeredUserByDay.registrationByDayName &&
          fenceTypeClassMapping[registeredUserByDay.registrationByDayName]
        ) {
          formattedFenceType =
            fenceTypeClassMapping[registeredUserByDay.registrationByDayName].fenceType
        }

        record['Fence Type'] = formattedFenceType
        record['USEF Section Code'] =
          rows.find((row) => row.class.uuid === registeredUserByDay.uuid)?.USEFSection.code || ''

        // class stats
        const classStats = (registeredUserByDay.uuid &&
          classStatsMap.get(registeredUserByDay.uuid)) || { entries: 0, competed: 0, completed: 0 }
        record['Number of entries'] = classStats.entries || ''
        record['Number that Competed'] = classStats.competed || ''
        record['Number that Completed'] = classStats.completed || ''

        record['Qualifier Dressage or Eventing (Dressage mandatory)'] =
          registeredUserByDay.qualifyFee && Number(registeredUserByDay.qualifyFee) > 0 ? 'Q' : ''

        const totalPrizeMoney = Number(prizeMoneyTotal) || 0
        const earningPercentage = Number(registeredUserByDay.score?.earning) || 0
        let actualAmount = 0
        if (totalPrizeMoney > 0 && earningPercentage > 0) {
          actualAmount = (totalPrizeMoney * earningPercentage) / 100
        }
        record['Prize Money Awarded'] = actualAmount > 0 ? toFixed(actualAmount, 2) : ''
        record['Prize Money Offered'] = Number(prizeMoneyTotal) || ''
        record['Placing'] = registeredUserByDay.isScratched
          ? 'S'
          : registeredUserByDay.score?.rank || ''
        record['Exhibitor Number '] = registeredUserByDay.backNumber || ''

        // Horse details
        record['Horse Passport Number'] =
          horse?.horsePassportNumber && horse?.horsePassportNumber !== 'N/A'
            ? horse.horsePassportNumber
            : ''
        record['Horse Name'] = horse?.horseName || ''

        record['Horse USEF Number'] = horse?.horseUsefNumber || ''
        record['Horse Registration/Breed Affiliate Number'] = horse
          ? getHorseBreedAffiliateNumbers(horse)
          : ''
        record['Horse FEI Number or Discipline Affiliate Number (USDF, USEA, etc.)'] = horse
          ? getHorseDisciplineAffiliateNumbers(horse)
          : ''

        record['Horse Year of Birth'] =
          new Date(horse?.horseDob as Date).getFullYear() > 0
            ? new Date(horse?.horseDob as Date).getFullYear()
            : ''
        record['Horse Breed'] =
          horseBreedList
            .filter((item) => horse?.horseBreed?.includes(item.value))
            .map((item) => item.label)
            .join(', ') || ''
        // Rider details
        record['Rider USEF Number'] = rider?.userUSEF?.documentNumber || ''
        record['Rider Name'] = rider?.id ? getUserFullName(rider) : ''
        record['Rider Breed Affiliate No. (IAHA, AHA, AMA, etc.)'] = rider
          ? getUserAffiliateNumbers(rider, BREED_AFFILIATE_FIELDS)
          : ''
        record[
          'Rider Discipline Number or FEI Number or Additional Affiliate Number (USDF, USEA, etc.)'
        ] = rider ? getUserAffiliateNumbers(rider, DISCIPLINE_AFFILIATE_FIELDS) : ''
        const riderAddress = parseAddress(rider?.userAddress || '')
        record['Rider Street Address'] = riderAddress?.street || rider?.userAddress || ''
        record['Rider City'] = riderAddress?.city || ''
        record['Rider State'] = riderAddress?.state || ''
        record['Rider Zip'] = riderAddress?.zip || ''
        record['FEI Rider Nationality'] =
          CountryList.find((item) => item.value === rider?.userNationality)?.label || ''
        record['Riders Email Address'] = rider?.userEmail || ''

        // Owner details
        record['Owner USEF Number'] = horseRiderOwner?.userUSEF?.documentNumber || ''
        record['Owner Name'] = horseRiderOwner?.id ? getUserFullName(horseRiderOwner) : ''
        record['Owner Breed Affiliate No. (IAHA, AHA, AMA, etc.)'] = horseRiderOwner
          ? getUserAffiliateNumbers(horseRiderOwner, BREED_AFFILIATE_FIELDS)
          : ''
        record[
          'Owner Discipline Number or FEI Number or Additional Affiliate Number (USDF, USEA, etc.)'
        ] = horseRiderOwner
          ? getUserAffiliateNumbers(horseRiderOwner, DISCIPLINE_AFFILIATE_FIELDS)
          : ''
        const ownerAddress = parseAddress(horseRiderOwner?.userAddress || '')
        record['Owner Street Address'] = ownerAddress?.street || horseRiderOwner?.userAddress || ''
        record['Owner City'] = ownerAddress?.city || ''
        record['Owner State'] = ownerAddress?.state || ''
        record['Owner Zip'] = ownerAddress?.zip || ''
        record['Owner Email Address'] = horseRiderOwner?.userEmail || ''

        // Trainer details
        record['Trainer USEF Number'] = horseRiderTrainer?.userUSEF?.documentNumber || ''
        record['Trainer Name'] = horseRiderTrainer?.id ? getUserFullName(horseRiderTrainer) : ''
        record['Trainer Affiliate Number (USDF, AHA, IAHA, etc.)'] = horseRiderTrainer
          ? getUserAffiliateNumbers(horseRiderTrainer, BREED_AFFILIATE_FIELDS)
          : ''
        const trainerAddress = parseAddress(horseRiderTrainer?.userAddress || '')
        record['Trainer Address'] = trainerAddress?.street || horseRiderTrainer?.userAddress || ''
        record['Trainer City'] = trainerAddress?.city || ''
        record['Trainer State'] = trainerAddress?.state || ''
        record['Trainer Zip'] = trainerAddress?.zip || ''

        // Add judge information
        // We need to handle this carefully to avoid TypeScript errors
        for (const [key, value] of Object.entries(judgeInfo)) {
          if (requiredColumns.includes(key as any)) {
            ;(record as any)[key] = value
          }
        }

        // Dressage information
        record['Dressage Score (Total)'] = registeredUserByDay.score?.totalPoints || ''
        record['Dressage Percentage (Total)'] = `${
          dressagePercentageTotal > 0 && dressagePercentageTotal !== Infinity
            ? toFixed(dressagePercentageTotal, 3)
            : 0
        }%`
        record['Dressage Level'] =
          rows.find((row) => row.class.uuid === registeredUserByDay.uuid)?.USEFSection.name || ''
        record['Dressage Rider Status'] =
          amateurList.find((item) => item.value === rider?.userAmateur)?.label || ''

        data.push(record)
      })

      data.sort((a, b) => {
        const horseNameA: string = String(a['Horse Name'] || '')
        const horseNameB: string = String(b['Horse Name'] || '')

        if (horseNameA !== horseNameB) {
          return horseNameA.localeCompare(horseNameB)
        }

        // If horse names are the same, sort by class number
        const classNumberA = Number(a['Class Number']) || 0
        const classNumberB = Number(b['Class Number']) || 0
        return classNumberA - classNumberB
      })

      // Convert data to a worksheet format
      const headers = requiredColumns
      const sheetRows = [
        // Create a new array of uppercase headers
        headers.map((header) => header.toUpperCase()) as unknown as string[],
        ...data.map((record) => headers.map((header) => record[header] || '')),
      ]

      // Calculate column widths
      const columnWidths = headers.map((header) => {
        return {
          wch: Math.max(
            header.length,
            ...data.map((record) => (record[header] ? String(record[header]).length : 0)),
            30 // Minimum width
          ),
        }
      })

      // Create worksheet
      const ws = XLSX.utils.aoa_to_sheet(sheetRows)
      ws['!cols'] = columnWidths

      // Create a workbook and add the worksheet
      const wb = XLSX.utils.book_new()
      XLSX.utils.book_append_sheet(wb, ws, 'USEF Results')

      // Write the workbook to an Excel file
      XLSX.writeFile(wb, `Event Reports - USEF Results - ${event?.eventName}.xlsx`)
    } else {
      toastFunctions.success({
        message: MESSAGES_CONST.REPORT_EXPORT_IS_READY,
      })
    }
  }

  const columns = [
    {
      name: (
        <span className="text-SeabiscuitDark200ThemeColor font-semibold text-sm">
          <span className="whitespace-nowrap">Class</span>
        </span>
      ),
      cell: (row: IUSEFResultsReport) => (
        <div
          className={clsx(
            'my-2 p-2 min-h-[56px] w-full rounded-md flex items-center border border-SeabiscuitLightThemeColorD3',
            row.discipline && row.USEFSection.code && 'bg-SeabiscuitGrayThemeColor'
          )}
        >
          {row.class.name}
        </div>
      ),
      width: '33.3%',
    },
    {
      name: (
        <span className="text-SeabiscuitDark200ThemeColor font-semibold text-sm">
          <span className="whitespace-nowrap">Discipline</span>
        </span>
      ),
      cell: (row: IUSEFResultsReport, index: number) => (
        <div className="flex items-center w-full pl-2">
          <Select
            className="w-full"
            classNamePrefix="clinic-select"
            isClearable={false}
            isSearchable={true}
            placeholder="Select Discipline"
            menuPlacement="auto"
            menuPortalTarget={document.body}
            styles={{
              control: (baseStyles) => ({
                ...baseStyles,
                borderColor: '#D3DAEE',
                backgroundColor: row.discipline && row.USEFSection.code ? '#F6F7FB' : '#fff',
              }),
            }}
            options={Object.keys(sectionCodesByDisciplines).map((discipline) => ({
              label: discipline,
              value: discipline,
            }))}
            value={row.discipline ? { label: row.discipline, value: row.discipline } : null}
            onChange={async (value) => {
              rows[index].discipline = value?.value ?? ''
              rows[index].USEFSection = { code: '', name: '' }
              setRows([...rows])
              await saveEventReport(rows)
            }}
          />
        </div>
      ),
      width: '33.3%',
    },
    {
      name: (
        <span className="text-SeabiscuitDark200ThemeColor font-semibold text-sm">
          <span className="whitespace-nowrap">Section codes</span>
        </span>
      ),
      cell: (row: IUSEFResultsReport, index: number) => {
        let sectionCodesOptions: { class: string; code: string }[]

        if (row.discipline) {
          sectionCodesOptions = sectionCodesByDisciplines[row.discipline as keyof IUSEFESection]
        } else {
          sectionCodesOptions = []
        }
        return (
          <div className="flex items-center w-full pl-2">
            <Select
              className="w-full"
              classNamePrefix="clinic-select"
              isClearable={false}
              isSearchable={true}
              placeholder={row.discipline ? 'Select Section Code' : 'Select a Discipline'}
              menuPlacement="auto"
              menuPortalTarget={document.body}
              styles={{
                control: (baseStyles) => ({
                  ...baseStyles,
                  borderColor: '#D3DAEE',
                  backgroundColor: row.discipline && row.USEFSection.code ? '#F6F7FB' : '#fff',
                }),
              }}
              options={
                sectionCodesOptions
                  ? sectionCodesOptions.map((discipline) => ({
                      label: discipline.class,
                      value: discipline.code,
                    }))
                  : []
              }
              value={
                row.USEFSection.code
                  ? { label: row.USEFSection.name, value: row.USEFSection.code }
                  : null
              }
              onChange={async (value) => {
                rows[index].USEFSection = value?.value
                  ? { code: value.value, name: value.label }
                  : { code: '', name: '' }
                setRows([...rows])
                await saveEventReport(rows)
              }}
            />
          </div>
        )
      },
      width: '33.3%',
    },
  ]

  return (
    <ReportsTabWrapper
      title="USEF Results"
      description="Match classes to section codes to print"
      activeOption={activeOption}
      setActiveOption={setActiveOption}
      onBack={() => setActiveResult('')}
      onExport={onExport}
      isReadyToExport={isReadyToExport}
      filledRows={
        rows.length - rows.filter((row) => row.discipline && row.USEFSection?.code).length
      }
      additional={
        <Select
          value={
            discipline
              ? {
                  label: discipline,
                  value: discipline,
                }
              : null
          }
          options={Object.keys(sectionCodesByDisciplines).map((discipline) => ({
            label: discipline,
            value: discipline,
          }))}
          className=" min-w-[220px]"
          isSearchable={false}
          placeholder="Discipline"
          formatOptionLabel={formatOptionLabel}
          styles={customSelectStyles}
          onChange={(value) => {
            setDiscipline((value as MyOptionType)?.value as IReports)
          }}
          components={{
            DropdownIndicator: (props_) => {
              return (
                <components.DropdownIndicator {...props_} className="[&>svg>path]:fill-[#122B46]" />
              )
            },
            IndicatorSeparator: () => null,
          }}
        />
      }
    >
      {!loading ? (
        <InfiniteScrollDataTable
          hasMore={false}
          className="exhibitorListTable"
          columns={columns}
          data={rows}
        />
      ) : (
        <div className="flex items-center justify-center p-4">
          <AutorenewIcon fontSize="small" className="animate-spin" />
        </div>
      )}
    </ReportsTabWrapper>
  )
}
